import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Music, Users, Award, Globe } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">About Us</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">About Soundfield</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Soundfield is your premier destination for music reviews, industry insights, and the latest sounds shaping our world. We're passionate about connecting artists with audiences through thoughtful journalism and expert analysis.
          </p>
        </div>

        {/* Mission Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
            <p className="text-muted-foreground mb-4">
              At Soundfield, we believe music is the universal language that connects us all. Our mission is to provide in-depth coverage of the music industry, from breaking artist interviews to comprehensive album reviews and behind-the-scenes stories.
            </p>
            <p className="text-muted-foreground mb-4">
              We strive to discover emerging talents, analyze industry trends, and give our readers the insights they need to stay connected with the ever-evolving world of music.
            </p>
            <p className="text-muted-foreground">
              Whether you're a music enthusiast, industry professional, or casual listener, Soundfield offers something for everyone who loves great music and compelling stories.
            </p>
          </div>
          <div className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  <Music className="h-4 w-4 inline mr-2" />
                  Album Reviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Comprehensive analysis of the latest releases
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  <Users className="h-4 w-4 inline mr-2" />
                  Artist Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Exclusive interviews and behind-the-scenes content
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  <Globe className="h-4 w-4 inline mr-2" />
                  Industry News
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Breaking news and trends from the music world
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Quality Content
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We're committed to delivering high-quality, well-researched content that respects both artists and readers.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Community
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Building a community of music lovers who share passion, discovery, and meaningful conversations about music.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Music className="h-5 w-5" />
                  Authenticity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Honest reviews and genuine storytelling that captures the essence of the music and artists we cover.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Contact CTA */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">Get in Touch</h3>
              <p className="text-muted-foreground mb-6">
                Have a story idea or want to collaborate? We'd love to hear from you.
              </p>
              <a 
                href="/contact" 
                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Contact Us
              </a>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 py-12">
        <div className="container">
          <div className="text-center text-sm text-muted-foreground">
            © 2024 Soundfield. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;